import type {CopilotAssignmentRepository} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {useCreateTask} from '@github-ui/agent-sessions/hooks/useCreateTask'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {createContext, memo, use, useCallback, useMemo, useState} from 'react'

import {ItemType} from '../api/memex-items/item-type'
import {useAddMemexItem} from '../hooks/use-add-memex-item'
import {repositoryWithOwnerFromString} from '../models/repository'
import {useCreateMemexItem} from '../state-providers/memex-items/use-create-memex-item'
import {useRepositories} from '../state-providers/repositories/use-repositories'
import {Resources} from '../strings'
import type {OmnibarItemAttrs} from './omnibar/types'
import useToasts, {ToastType} from './toasts/use-toasts'

type CreateJobResponse = {
  session_id: string
  job_id?: string
  updated_at?: string
  pull_request?: {
    id: number
    global_relay_id: string
    title: string
    body: string
  }
  issue?: {
    id: number
    global_relay_id: string
    title: string
    body: string
  }
}

const MEMEX_EVENT_TYPE = 'memex-project'
const noOpAddDraftItem = () => Promise.resolve(null)

type StartCopilotTaskCreator = (
  problemStatement?: string,
  repository?: CopilotAssignmentRepository,
  newItemAttributes?: OmnibarItemAttrs,
) => Promise<void>

type CopilotTaskCreatorContextValue = {
  start: StartCopilotTaskCreator
  prefetch: () => void
}

const CopilotTaskCreatorContext = createContext<CopilotTaskCreatorContextValue | null>(null)

export const useStartCopilotTaskCreator = () => use(CopilotTaskCreatorContext)

interface CopilotTaskCreatorProps {
  onClose: () => void
  repository: CopilotAssignmentRepository
  problemStatement: string
  repositoryId?: number
  newItemAttributes?: OmnibarItemAttrs
}

const CopilotTaskCreator = ({
  onClose,
  repository,
  problemStatement,
  repositoryId,
  newItemAttributes,
}: CopilotTaskCreatorProps) => {
  const {addToast} = useToasts()
  const {createMemexItem} = useCreateMemexItem()

  const addItemToView = useAddMemexItem({
    updateActions: newItemAttributes?.updateColumnActions,
    previousItemId: newItemAttributes?.previousItemId,
    onAddDraftItem: noOpAddDraftItem,
    onAddItem: useCallback(
      (item, repoId, memexProjectColumnValues, localColumnValues, previousMemexProjectItemId) =>
        createMemexItem(
          {
            contentType: item.type,
            content: {id: item.id, repositoryId: repoId},
            memexProjectColumnValues,
            localColumnValues,
            previousMemexProjectItemId,
          },
          newItemAttributes?.groupId,
          newItemAttributes?.secondaryGroupId,
        ),
      [createMemexItem, newItemAttributes?.groupId, newItemAttributes?.secondaryGroupId],
    ),
  })
  const {createTaskAsync} = useCreateTask<CreateJobResponse>({
    onError: error => {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: ToastType.error,
        message:
          error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? error.message
            : 'Failed to start agent session',
      })
    },
  })

  const handleSubmit = useCallback(
    async (
      _graphQLId: string,
      selectedBranch?: string,
      content?: string,
      selectedAgent?: SelectedAgent,
      selectedModel?: string,
      selectedRepoNwo?: string,
      selectedRepoId?: number,
    ) => {
      const nwo = selectedRepoNwo ?? `${repository.owner.login}/${repository.name}`
      const repoId = selectedRepoId ?? repositoryId
      const repoOwner = selectedRepoNwo ? selectedRepoNwo.split('/')[0] : repository.owner.login
      const statement = content || problemStatement

      try {
        const taskResult = await createTaskAsync({
          nwo,
          baseRef: selectedBranch,
          selectedAgent,
          problemStatement: statement,
          title: statement.slice(0, 100),
          eventType: MEMEX_EVENT_TYPE,
          modelId: selectedModel,
        })

        // Handle Issue artifact (preferred)
        if (taskResult.issue?.id) {
          if (!repoId) {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: ToastType.warning,
              message: 'Agent session started, but the issue could not be added to the project',
            })
          } else {
            await addItemToView({
              type: ItemType.Issue,
              id: taskResult.issue.id,
              repositoryId: repoId,
              repositoryOwner: repoOwner ?? '',
            })
          }
        }
        // Handle Pull Request artifact
        else if (taskResult.pull_request?.id) {
          if (!repoId) {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: ToastType.warning,
              message: 'Agent session started, but the pull request could not be added to the project',
            })
          } else {
            await addItemToView({
              type: ItemType.PullRequest,
              id: taskResult.pull_request.id,
              repositoryId: repoId,
              repositoryOwner: repoOwner ?? '',
            })
          }
        }
        // No artifact was returned - this is unexpected
        else {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: ToastType.warning,
            message: 'Agent session started successfully, but no pull request or issue was created',
          })
        }

        onClose()
      } catch (error) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: ToastType.error,
          message:
            error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
              ? error.message
              : 'Failed to start agent session',
        })
      }
    },
    [repository, problemStatement, repositoryId, createTaskAsync, addItemToView, onClose, addToast],
  )

  return (
    <CopilotAssignmentModal
      isOpen
      onClose={onClose}
      repository={repository}
      onSubmit={handleSubmit}
      title={Resources.createCopilotPRTitle}
      description=""
      promptPlaceholder={Resources.createCopilotPRPromptPlaceholder}
      promptLabelHidden
      promptRequired
    />
  )
}

export const CopilotTaskCreatorProvider = memo(function CopilotTaskCreatorProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [creatorProps, setCreatorProps] = useState<CopilotTaskCreatorProps | undefined>()
  const {suggestRepositories} = useRepositories()

  const closeModal = useCallback(() => {
    setCreatorProps(undefined)
  }, [])

  const startCopilotTaskCreator = useCallback<StartCopilotTaskCreator>(
    async (problemStatement = '', repository?: CopilotAssignmentRepository, newItemAttributes?: OmnibarItemAttrs) => {
      let repo = repository
      let repoId: number | undefined
      if (!repo) {
        // Get the first suggested repository if none provided
        const repos = await suggestRepositories()
        const firstRepo = repos.at(0)
        if (firstRepo?.nameWithOwner) {
          const parsed = repositoryWithOwnerFromString(firstRepo.nameWithOwner)
          if (parsed) {
            repo = {
              name: parsed.name,
              owner: {login: parsed.owner},
            }
            repoId = firstRepo.id
          }
        }
      }

      if (repo) {
        setCreatorProps({
          onClose: closeModal,
          repository: repo,
          problemStatement,
          repositoryId: repoId,
          newItemAttributes,
        })
      }
    },
    [suggestRepositories, closeModal],
  )

  const prefetch = useCallback(() => {
    // Prefetch repositories for faster modal opening
    void suggestRepositories()
  }, [suggestRepositories])

  const value: CopilotTaskCreatorContextValue = useMemo(
    () => ({
      start: startCopilotTaskCreator,
      prefetch,
    }),
    [startCopilotTaskCreator, prefetch],
  )

  return (
    <CopilotTaskCreatorContext value={value}>
      {children}
      {creatorProps && <CopilotTaskCreator {...creatorProps} />}
    </CopilotTaskCreatorContext>
  )
})
